// Where a completed sign-in goes (FR-034; P-AUTH-2, P-JOIN-6; data-model §7; research R8). The steps
// run in a fixed order and the first that answers wins; later features insert steps before
// roleLanding. The result is held as a one-shot destination read by the (signed-out) layout.
//
// Hand-off to 003 (school join link): joinContinuationHref is unchanged. 003 did not add a step here:
// "no students → Enter school code" lives in src/join/ParentHomeGate.tsx around parent Home (R7).
// No 002 flow clears or rewrites the pending join except the user's own Log out (T235, T236).
import type { PendingJoin } from "@/join/pendingJoin";

import { landingHref, resolveLanding, resolveRouteAccess } from "./access";
import { servedRoles } from "./store";
import type { Area, Platform, SignedInSession } from "./types";

export type PostSignInContext = {
  session: SignedInSession;
  platform: Platform;
  needsRolePick: boolean;
  pendingJoin: PendingJoin | null;
  /** Already sanitised (001 sanitizeReturnTo). */
  returnTo: string | null;
};

export type PostSignInStep = {
  id: string;
  resolve(ctx: PostSignInContext): string | null;
};

/** 003 may change the body only. */
export function joinContinuationHref(code: string): string {
  return `/join/${code}`;
}

const PUBLIC = ["/verify-email", "/reset-password", "/accept-invite"];
const SIGNED_OUT = ["/welcome", "/login", "/sign-up", "/forgot-password"];

/** The route area of an app path (routes.md). */
export function areaOf(path: string): Area {
  const bare = path.split(/[?#]/)[0] ?? path;
  if (bare === "/school" || bare.startsWith("/school/")) return "school";
  if (PUBLIC.includes(bare) || bare.startsWith("/join/")) return "public";
  if (SIGNED_OUT.includes(bare)) return "signedOut";
  return "parent";
}

export const postSignInSteps: readonly PostSignInStep[] = [
  // A refusal never reaches a destination: completeSignIn shows its message on /login instead.
  { id: "refusal", resolve: () => null },
  {
    id: "pendingJoin",
    resolve: ({ session, pendingJoin }) =>
      pendingJoin && servedRoles(session.roles).includes("PARENT")
        ? joinContinuationHref(pendingJoin.code)
        : null,
  },
  {
    id: "returnTo",
    resolve: ({ session, platform, returnTo }) =>
      returnTo &&
      resolveRouteAccess(session, areaOf(returnTo), platform, returnTo).kind ===
        "allow"
        ? returnTo
        : null,
  },
  {
    id: "roleLanding",
    resolve: ({ session, platform, needsRolePick }) =>
      needsRolePick
        ? "/choose-role"
        : (landingHref(resolveLanding(session, platform)) ?? "/"),
  },
];

export function resolvePostSignIn(
  ctx: PostSignInContext,
  steps: readonly PostSignInStep[] = postSignInSteps,
): string {
  for (const step of steps) {
    const href = step.resolve(ctx);
    if (href !== null) return href;
  }
  return "/";
}
